export type ConcurrencyOutcome<T, R> =
  | { item: T; status: 'fulfilled'; value: R }
  | { item: T; status: 'rejected'; reason: unknown };

/**
 * Runs `worker` over every item with at most `limit` calls in flight at once, resolving every
 * item independently — one item throwing never aborts the others (`Promise.allSettled` semantics,
 * just bounded). Used for bulk actions (bulk delete/unshare/remove/add) so a large selection
 * doesn't fire dozens of simultaneous requests, and by the playlist feed page to resolve a
 * playlist's videos without hammering the public /watch lookup all at once.
 *
 * Result order is completion order, not input order — callers match back to the original item via
 * each outcome's `item`, not its position.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  worker: (item: T, index: number) => Promise<R>,
  limit: number,
): Promise<ConcurrencyOutcome<T, R>[]> {
  const queue = items.map((item, index) => ({ item, index }));
  const results: ConcurrencyOutcome<T, R>[] = [];

  async function run() {
    let next = queue.shift();
    while (next) {
      const { item, index } = next;
      try {
        const value = await worker(item, index);
        results.push({ item, status: 'fulfilled', value });
      } catch (reason) {
        results.push({ item, status: 'rejected', reason });
      }
      next = queue.shift();
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, run));
  return results;
}
